import { useGSAP } from "@gsap/react"
import gsap from "gsap"
import { ScrollTrigger } from "gsap/ScrollTrigger"
import type { RefObject } from "react"

gsap.registerPlugin(ScrollTrigger, useGSAP)

type Refs = {
  sectionRef: RefObject<HTMLElement | null>
  bubbleRef: RefObject<HTMLDivElement | null>
  bodyRef: RefObject<HTMLDivElement | null>
  tailRef: RefObject<HTMLSpanElement | null>
}

/**
 * Pins a section while the chat bubble + tail + body lines reveal via scrub.
 * Mobile (<768px) skips the pin and plays a one-shot enter animation instead.
 * Honors prefers-reduced-motion by clearing initial-hidden styles.
 */
export function usePinnedReveal({
  sectionRef,
  bubbleRef,
  bodyRef,
  tailRef,
}: Refs) {
  useGSAP(
    () => {
      if (!sectionRef.current) return

      if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
        gsap.set(
          [
            bubbleRef.current,
            bodyRef.current?.children ?? null,
            tailRef.current,
          ],
          { clearProps: "all" }
        )
        return
      }

      const setInitial = () => {
        gsap.set(bubbleRef.current, {
          opacity: 0,
          y: 24,
          scale: 0.985,
          transformOrigin: "bottom center",
        })
        gsap.set(tailRef.current, { opacity: 0, scale: 0.5 })
        if (bodyRef.current) {
          gsap.set(bodyRef.current.children, { opacity: 0, y: 8 })
        }
      }

      const mm = gsap.matchMedia()

      mm.add("(min-width: 768px)", () => {
        setInitial()
        const tl = gsap.timeline()
        tl.to(
          bubbleRef.current,
          { opacity: 1, y: 0, scale: 1, duration: 0.3, ease: "power3.out" },
          0.3
        ).to(
          tailRef.current,
          { opacity: 1, scale: 1, duration: 0.25, ease: "power3.out" },
          0.4
        )
        if (bodyRef.current) {
          tl.to(
            bodyRef.current.children,
            {
              opacity: 1,
              y: 0,
              duration: 0.35,
              stagger: 0.08,
              ease: "power2.out",
            },
            0.5
          )
        }

        ScrollTrigger.create({
          trigger: sectionRef.current,
          start: "top top",
          end: "+=140%",
          pin: true,
          scrub: 1,
          anticipatePin: 1,
          invalidateOnRefresh: true,
          animation: tl,
        })
      })

      mm.add("(max-width: 767px)", () => {
        setInitial()
        const tl = gsap.timeline({
          scrollTrigger: {
            trigger: sectionRef.current,
            start: "top 65%",
            once: true,
          },
        })
        tl.to(bubbleRef.current, {
          opacity: 1,
          y: 0,
          scale: 1,
          duration: 0.7,
          ease: "power3.out",
        }).to(
          tailRef.current,
          { opacity: 1, scale: 1, duration: 0.4, ease: "power3.out" },
          "<0.1"
        )
        if (bodyRef.current) {
          tl.to(
            bodyRef.current.children,
            {
              opacity: 1,
              y: 0,
              duration: 0.5,
              stagger: 0.12,
              ease: "power2.out",
            },
            "<0.1"
          )
        }
      })
    },
    { scope: sectionRef }
  )
}
